import { useCallback, useEffect, useRef } from 'react';
import { useSessionState } from '../../../context/sessionStateContext';
import { useCalibration } from '../../../context/calibrationContext';
import { calibrationConstants } from '../../../helpers/calibration-constants';

// Calibration <-> shared session state (BE-7 /session_state/).
//
// What is stored (PATCH, partial merge):
//   sub_step                         the clinician's calibration sub-screen: screen-calibration |
//                                    device | card | ruler | camera-calibration-selection |
//                                    camera-calibration | blindspot-camera-calibration
//   calibration.method               device | card | ruler
//   calibration.device               {make, model, screen_mm:[long, short]} (device method)
//   calibration.screen_calibrated / camera_calibrated
//   calibration.card_ratio           the resulting pixel density (mm per CSS px of the patient's
//                                    screen) for every method, so a re-joining clinician gets the
//                                    same stimulus sizes without re-calibrating
//   distance.mode (face|manual), distance.mm, distance.positioned
//
// The in-call calibration data itself still travels over the room socket
// (synchroniseCalibrationData); the session state only lets a re-joining participant restore it.

export const CLINICIAN_SUB_STEPS = [
  'calibration-selection',
  'screen-calibration',
  'device',
  'card',
  'ruler',
  'camera-calibration-selection',
  'camera-calibration',
  'blindspot-camera-calibration',
];

const quiet = (p) => (p && typeof p.catch === 'function' ? p.catch(() => null) : p);

/** Fire-and-forget session state patches for the calibration screens. */
export const useCalibrationSync = () => {
  const { patch, state } = useSessionState();
  const patchCalibration = useCallback((calibration) => quiet(patch({ calibration })), [patch]);
  const patchDistance = useCallback((distance) => quiet(patch({ distance })), [patch]);
  const patchSubStep = useCallback((subStep) => quiet(patch({ sub_step: subStep })), [patch]);
  return { state, patchCalibration, patchDistance, patchSubStep };
};

// Session-state distance.mode <-> calibrationData.methodSelected.
export const toDistanceMode = (method) => (method === 'faceRecognition' ? 'face' : 'manual');
export const fromDistanceMode = (mode) => (mode === 'manual' ? 'manual' : 'faceRecognition');

/** calibrationData fields to restore from a session state (empty object when nothing to do). */
export const restoredCalibrationData = (sessionState, calibrationData = {}) => {
  const c = sessionState?.calibration || {};
  const d = sessionState?.distance || {};
  const out = {};
  if (c.screen_calibrated && !calibrationData.screenCalibrated) {
    out.screenCalibrated = true;
    if (c.card_ratio > 0) {
      out.pixelDensity = c.card_ratio;
      out.circleDimensionsPx = Math.round(calibrationConstants.circleDimensionsReferenceMm / c.card_ratio);
    }
  }
  if (c.camera_calibrated && !calibrationData.cameraCalibrated) out.cameraCalibrated = true;
  if (d.mm > 0 && (d.positioned || d.mode === 'manual')) {
    out.distanceFromScreen = d.mm;
    out.methodSelected = fromDistanceMode(d.mode);
    const pixelDensity = out.pixelDensity || calibrationData.pixelDensity;
    if (pixelDensity) out.blindSpotVanishingXDistancePx = (d.mm * calibrationConstants.tangent15degrees) / pixelDensity;
  }
  return out;
};

/**
 * Clinician: once the session state has loaded, restore calibration flags / pixel density /
 * distance (a clinician re-joining mid-session) and the calibration sub-screen.
 */
export const useRestoreClinicianCalibration = (setCalibrationStep) => {
  const { state, status } = useSessionState();
  const calibration = useCalibration();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || status !== 'ready' || !calibration) return;
    done.current = true;
    const restore = restoredCalibrationData(state, calibration.calibrationData);
    if (Object.keys(restore).length) calibration.updateCalibrationData(restore);
    if (state?.step === 'calibration-selection' && CLINICIAN_SUB_STEPS.includes(state?.sub_step) && setCalibrationStep) {
      setCalibrationStep(state.sub_step);
    }
  }, [status, state, calibration, setCalibrationStep]);
};

/** Patient tab for a given session state (null = the waiting screens). */
export const patientTabFor = (sessionState, { desktop = false } = {}) => {
  const { step, sub_step: sub, distance } = sessionState || {};
  if (step === 'calibration-selection') {
    if (sub === 'screen-calibration' || sub === 'device') return desktop ? null : 'screen-calibration';
    if (['card', 'ruler', 'camera-calibration'].includes(sub)) return sub;
    return null;
  }
  if (step === 'set-distance' && distance?.mode !== 'manual') return 'pupillaryDistance';
  return null;
};

/** Patient: after a reload, land on the clinician's current calibration / distance screen. */
export const useRestorePatientTab = (activeTab, setActiveTab, options) => {
  const { state, status } = useSessionState();
  const done = useRef(false);
  const desktop = options?.desktop;
  useEffect(() => {
    if (done.current || status !== 'ready') return;
    done.current = true;
    if (activeTab && activeTab !== 'holding') return;
    const tab = patientTabFor(state, { desktop });
    if (tab) setActiveTab(tab);
  }, [status, state, activeTab, setActiveTab, desktop]);
};
